import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AppConstants } from '../commons/app-constants';
import { isOfflineCapableNative } from '../commons/utils/platform';
import { getSessionSnapshot, setSessionSnapshot, wipeOfflineDataIfDifferentUser } from '../offline/offline-db';

export interface PermissionDefinition {
  code: string;
  module: string;
  label: string;
  description: string;
  displayOrder: number;
  /** The module View right this code needs (null = stands alone). From the backend's
   *  PermissionCode.requiredViewRightFor() — the single source of truth, never duplicated here. */
  requiresCode?: string | null;
}

export interface UserPermissionsResponse {
  userId: number;
  permissions: string[];
  rbacEnabled: boolean;
  /** Non-null when this user's access is live-linked to a named AccessRole. */
  accessRoleId?: number | null;
  accessRoleName?: string | null;
  /** Current SUPER_ADMIN/ROOT-configured max days a native session may run without a
   *  successful live sync before forced logout — see syncSessionForGuard(). */
  maxOfflineDays: number;
  /** The calling user's own email — used by AuthSessionGuard's different-device-user wipe check. */
  email?: string | null;
  /** The ACTIVE tenant for this session (not necessarily the user's default company — multi-
   *  company switch reissues the token against the target tenant). Used together with email by
   *  the same wipe check: email alone cannot detect a company switch. */
  tenantId?: number | null;
}

const DEFAULT_MAX_OFFLINE_DAYS = 15;

/** Outcome of a AuthSessionGuard-driven session sync — see PermissionService.syncSessionForGuard(). */
export interface GuardSessionSyncResult {
  // 'live': fresh server response this call. 'cached': native offline fallback to the last
  // genuinely-live snapshot. 'never-synced': native + offline + no snapshot has EVER been
  // written on this device — AuthSessionGuard must refuse to proceed, not fail open. 'api-error-fail-
  // open': a real (non-network) API error, or web with no cache to fall back to — matches
  // loadMyPermissions()'s pre-existing fail-open behavior exactly.
  outcome: 'live' | 'cached' | 'never-synced' | 'api-error-fail-open';
  email: string | null;
  /** Null on every non-'live' outcome — the wipe check treats null as "identity unknown, don't
   *  act", exactly as it already does for email. Only a genuinely-live server response is
   *  authoritative enough to trigger a destructive wipe. */
  tenantId: number | null;
  maxOfflineDays: number;
  syncedAt: number | null;
}

export interface PermissionAuditEntry {
  permissionCode: string;
  action: 'GRANT' | 'REVOKE';
  doneBy: number;
  doneByName: string;
  doneAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly apiUrl = AppConstants.API_URL;

  private permissionsSubject = new BehaviorSubject<Set<string>>(new Set<string>());
  permissions$ = this.permissionsSubject.asObservable();

  // Tracks whether RBAC is active for the current user's tenant, straight from the same
  // /users/me/permissions response. Needed so PermissionGuard can tell "RBAC disabled → allow
  // by design" apart from "RBAC enabled, this user just has zero grants → deny" — an empty
  // permission set alone is ambiguous between those two cases.
  private rbacEnabledSubject = new BehaviorSubject<boolean>(true);

  // The current user's own live-linked role name, straight from the same /users/me/permissions
  // response — null for Owner/Admin (implicitly full access, no role assignment) and for
  // RBAC-off tenants (no role concept applies).
  private myAccessRoleNameSubject = new BehaviorSubject<string | null>(null);
  myAccessRoleName$ = this.myAccessRoleNameSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ─── Load current user's permissions (called post-login) ─────────────────

  private fetchMyPermissions(): Observable<UserPermissionsResponse> {
    return this.http.get<UserPermissionsResponse>(`${this.apiUrl}api/v1/users/me/permissions`);
  }

  // Fire-and-forget / cache-warm use (sidebar init/refresh, auth interceptor, payment-return
  // flows) — NEVER throws, always resolves. Several existing callers rely on exactly this via a
  // bare .subscribe() with no error handler (see e.g. payment-sucess.component.ts's own comment
  // to that effect) — do not change this method's error behavior. Guard-driven callers that need
  // to distinguish a network failure from a real API error must use syncSessionForGuard() below
  // instead, not this method.
  loadMyPermissions(): Observable<Set<string>> {
    return this.fetchMyPermissions().pipe(
      tap(r => this.rbacEnabledSubject.next(r.rbacEnabled ?? true)),
      tap(r => this.myAccessRoleNameSubject.next(r.accessRoleName ?? null)),
      map(r => new Set<string>(r.permissions ?? [])),
      tap(perms => this.permissionsSubject.next(perms)),
      catchError(() => {
        // On error (e.g. no subscription), treat as empty and RBAC-off — do not block app.
        // This said "RBAC-off" but set rbacEnabled=true, which (with an empty permission set)
        // actually means deny-everything — see the matching fix in performSessionSync()'s
        // failOpen(). Corrected to false so the code matches its own documented intent.
        this.rbacEnabledSubject.next(false);
        this.myAccessRoleNameSubject.next(null);
        return of(new Set<string>());
      }),
    );
  }

  refreshPermissions(): Observable<Set<string>> {
    return this.loadMyPermissions();
  }

  // ─── Guard-driven session sync (AuthSessionGuard + PermissionGuard, every guarded navigation) ─────
  // Single source of truth for permissions + rbacEnabled + accessRoleName + maxOfflineDays on
  // every navigation.
  //
  // The in-flight request is SHARED (same shareReplay+finalize pattern as the HTTP interceptor's
  // refreshRequest$). This is load-bearing, not an optimization: Angular runs every guard in a
  // route's canActivate array CONCURRENTLY — Router.runCanActivate wraps them in combineLatest
  // via prioritizedGuardValue(), and the array order only decides which RESULT wins, never the
  // execution order. So AuthSessionGuard and PermissionGuard both enter on the same tick, and
  // PermissionGuard cannot rely on "AuthSessionGuard ran first and left a fresh snapshot behind" — that
  // assumption was the cause of a real bug (found 2026-08-03): on a cold load PermissionGuard
  // read the BehaviorSubjects' initial values (empty permission set + rbacEnabled=true), denied
  // every gated route, and navigated to /unauthorized — which cancelled the in-flight navigation
  // and with it AuthSessionGuard's own /users/me/permissions request (visible as a "(canceled)" XHR).
  // Sharing lets PermissionGuard legitimately AWAIT this sync instead of racing it, while still
  // firing exactly one /users/me/permissions request per navigation — which is what the previous
  // "PermissionGuard must not call this" rule was really trying to protect.
  private guardSyncInFlight$: Observable<GuardSessionSyncResult> | null = null;

  syncSessionForGuard(): Observable<GuardSessionSyncResult> {
    if (!this.guardSyncInFlight$) {
      this.guardSyncInFlight$ = this.performSessionSync().pipe(
        shareReplay(1),
        finalize(() => { this.guardSyncInFlight$ = null; }),
      );
    }
    return this.guardSyncInFlight$;
  }

  private performSessionSync(): Observable<GuardSessionSyncResult> {
    return this.fetchMyPermissions().pipe(
      map((r) => {
        this.rbacEnabledSubject.next(r.rbacEnabled ?? true);
        this.myAccessRoleNameSubject.next(r.accessRoleName ?? null);
        this.permissionsSubject.next(new Set<string>(r.permissions ?? []));
        const maxOfflineDays = r.maxOfflineDays > 0 ? r.maxOfflineDays : DEFAULT_MAX_OFFLINE_DAYS;
        const syncedAt = Date.now();
        if (isOfflineCapableNative()) {
          setSessionSnapshot({
            permissions: r.permissions ?? [],
            rbacEnabled: r.rbacEnabled ?? true,
            accessRoleName: r.accessRoleName ?? null,
            maxOfflineDays,
            syncedAt,
          }).catch(() => { /* best-effort cache write — a failed write here just means the next
            offline fallback has slightly staler data, not a correctness issue */ });
        }
        const result: GuardSessionSyncResult = {
          outcome: 'live',
          email: r.email ?? null,
          tenantId: r.tenantId ?? null,
          maxOfflineDays,
          syncedAt,
        };
        return result;
      }),
      catchError((err) => {
        const isNetworkFailure = err?.status === 0 || err?.status === undefined;
        const failOpen = (): Observable<GuardSessionSyncResult> => {
          // rbacEnabled=FALSE, not true. An empty permission set combined with rbacEnabled=true
          // means "RBAC is on and this user has zero grants" — i.e. deny everything, which is
          // fail-CLOSED and the exact opposite of this branch's name and intent. That mismatch
          // was harmless only while PermissionGuard read a stale snapshot instead of awaiting
          // this result; now that it awaits (see syncSessionForGuard above), any transient API
          // error here would otherwise hard-redirect the user to /unauthorized. RBAC-off is the
          // documented "do not block app" behavior, and the backend still enforces every
          // permission server-side via @PreAuthorize regardless of what the UI allows.
          this.rbacEnabledSubject.next(false);
          this.myAccessRoleNameSubject.next(null);
          this.permissionsSubject.next(new Set<string>());
          return of({ outcome: 'api-error-fail-open', email: null, tenantId: null, maxOfflineDays: DEFAULT_MAX_OFFLINE_DAYS, syncedAt: null });
        };
        if (!isNetworkFailure || !isOfflineCapableNative()) {
          // Defense-in-depth for the same bug class as [[project-different-user-wipe-race]]/the
          // 2026-08-27 onboarding-window leak: a genuine (non-network) API error still means the
          // server authenticated the request — the JWT/cookie is valid, we just failed to compute
          // permissions. If the error body happens to carry identity fields (not guaranteed today
          // — no backend change shipped alongside this, so errEmail is always undefined right now
          // and this branch is currently a no-op — kept ready for if/when a backend error body
          // adds them), still run the same different-user wipe check AuthSessionGuard normally
          // runs on a 'live' result, so a wipe/marker-update isn't silently skipped just because
          // this one call errored. RBAC itself still fails open exactly as before — this only adds
          // the wipe side effect. GENUINELY AWAITED before failOpen()'s result is emitted (not
          // fire-and-forget) — matches the 'live' branch's guarantee that navigation cannot
          // proceed until the wipe has actually finished; a fire-and-forget wipe here would let a
          // route render before the cache was actually cleared, reintroducing exactly the kind of
          // race this whole fix exists to close.
          const errEmail = err?.error?.email;
          const errTenantId = err?.error?.tenantId;
          if (typeof errEmail === 'string' && errEmail) {
            return from(
              wipeOfflineDataIfDifferentUser(errEmail, typeof errTenantId === 'number' ? errTenantId : null)
                .catch(() => { /* best-effort, same as every other wipe call site */ }),
            ).pipe(switchMap(() => failOpen()));
          }
          // Real API error (e.g. no subscription) — or web, which has no offline cache to fall
          // back to either way — fail open exactly like loadMyPermissions() already does.
          return failOpen();
        }
        return from(getSessionSnapshot()).pipe(
          switchMap((snapshot) => {
            if (!snapshot) {
              // Never successfully synced on this device, and can't reach the server now —
              // AuthSessionGuard must refuse to proceed rather than guess at either extreme.
              return of<GuardSessionSyncResult>({ outcome: 'never-synced', email: null, tenantId: null, maxOfflineDays: DEFAULT_MAX_OFFLINE_DAYS, syncedAt: null });
            }
            this.rbacEnabledSubject.next(snapshot.rbacEnabled);
            this.myAccessRoleNameSubject.next(snapshot.accessRoleName);
            this.permissionsSubject.next(new Set<string>(snapshot.permissions));
            return of<GuardSessionSyncResult>({ outcome: 'cached', email: null, tenantId: null, maxOfflineDays: snapshot.maxOfflineDays, syncedAt: snapshot.syncedAt });
          }),
        );
      }),
    );
  }

  clearPermissions(): void {
    this.permissionsSubject.next(new Set<string>());
    this.rbacEnabledSubject.next(true);
    this.myAccessRoleNameSubject.next(null);
  }

  /** Snapshot of whether RBAC is active for the current user's tenant, from the last load. */
  isRbacEnabledSnapshot(): boolean {
    return this.rbacEnabledSubject.value;
  }

  /** Snapshot of the current user's own live-linked role name, from the last load. Null for
   *  Owner/Admin or RBAC-off tenants — callers combine this with their own Owner/Admin check
   *  to render the right fallback label. */
  myAccessRoleNameSnapshot(): string | null {
    return this.myAccessRoleNameSubject.value;
  }

  // ─── Permission checks ────────────────────────────────────────────────────

  has(code: string): boolean {
    return this.permissionsSubject.value.has(code);
  }

  hasAny(codes: string[]): boolean {
    return codes.some(c => this.permissionsSubject.value.has(c));
  }

  hasAll(codes: string[]): boolean {
    return codes.every(c => this.permissionsSubject.value.has(c));
  }

  getSnapshot(): Set<string> {
    return this.permissionsSubject.value;
  }

  /** Same fail-open rule PermissionGuard already enforces: RBAC disabled for this tenant -> show
   *  everything (zero change from a coarser gate). Use this (not has()) for any UI element that
   *  used to be ungated and is now being split into a finer-grained permission code — see
   *  Dashboard V6's per-widget gating for the reference use case. */
  hasEffective(code: string): boolean {
    return !this.isRbacEnabledSnapshot() || this.has(code);
  }

  // ─── Admin management APIs ────────────────────────────────────────────────

  getAllDefinitions(): Observable<PermissionDefinition[]> {
    return this.http.get<PermissionDefinition[]>(`${this.apiUrl}api/v1/permission-definitions`);
  }

  getUserPermissions(userId: number): Observable<UserPermissionsResponse> {
    return this.http.get<UserPermissionsResponse>(`${this.apiUrl}api/v1/users/${userId}/permissions`);
  }

  replaceUserPermissions(userId: number, permissionCodes: string[]): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}api/v1/users/${userId}/permissions`, permissionCodes).pipe(
      tap(() => {
        // If updating own permissions, refresh local cache
      }),
    );
  }

  getAuditLog(userId: number): Observable<PermissionAuditEntry[]> {
    return this.http.get<PermissionAuditEntry[]>(`${this.apiUrl}api/v1/users/${userId}/permissions/audit`);
  }

  getRbacOverview(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}api/v1/rbac/overview`);
  }

  getRbacStatus(): Observable<{ rbacEnabled: boolean; tenantPk: number }> {
    return this.http.get<{ rbacEnabled: boolean; tenantPk: number }>(`${this.apiUrl}api/v1/rbac/status`);
  }

  /** Non-admin endpoint — any authenticated user can check if RBAC is active for their tenant. */
  getRbacEnabled(): Observable<{ rbacEnabled: boolean }> {
    return this.http.get<{ rbacEnabled: boolean }>(`${this.apiUrl}api/v1/rbac/enabled`);
  }

  enableRbac(): Observable<{ rbacEnabled: boolean }> {
    return this.http.post<{ rbacEnabled: boolean }>(`${this.apiUrl}api/v1/rbac/enable`, {});
  }

  disableRbac(): Observable<{ rbacEnabled: boolean }> {
    return this.http.post<{ rbacEnabled: boolean }>(`${this.apiUrl}api/v1/rbac/disable`, {});
  }

  /**
   * Resolves the first route the current user is permitted to land on.
   * Always makes a fresh API call so post-login state is accurate.
   * Priority: Dashboard → Tally → Shipments → BP → Reports → Storage → Company → /unauthorized
   */
  getFirstAccessibleRoute(): Observable<string> {
    const PRIORITY: Array<[string, string]> = [
      ['DASHBOARD_ACCESS', '/dashboard-new'],
      ['TALLY_ACCESS',     '/new-tallysheet'],
      ['SHIPMENT_ACCESS',  '/consignments'],
      ['BP_ACCESS',        '/business-partners'],
      ['REPORT_ACCESS',    '/reports'],
      ['STORAGE_ACCESS',   '/storage'],
      ['EXTRAS_ACCESS',    '/company'],
    ];
    return this.loadMyPermissions().pipe(
      map(perms => {
        for (const [perm, route] of PRIORITY) {
          if (perms.has(perm)) return route;
        }
        return '/unauthorized';
      }),
      catchError(() => of('/dashboard-new')),
    );
  }

  /** Apply a named role template to a user (FIELD_OPERATOR, SUPERVISOR, ANALYST, MANAGER, CUSTOM). */
  applyTemplate(userId: number, template: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}api/v1/users/${userId}/permissions/template`, { template });
  }

  /** Detect the current role template for a user based on their permissions. */
  detectTemplate(userId: number): Observable<{ template: string }> {
    return this.http.get<{ template: string }>(`${this.apiUrl}api/v1/users/${userId}/permissions/template`);
  }
}
